import { convertiUtsToDate } from '../../GrapheXT/GrapheXT';
import { LABEL_COLORS, MANUAL_COLOR } from './labelConstants';
import { buildIconDataUri } from './iconSvgPaths';
import { CATEGORY_SVG_KEYS } from './iaCategories';

export const buildAnnotation = ({
  xDate,
  paperY,
  label,
  dateStr,
  note,
  color,
  valLine = '',
}) => ({
  x: xDate,
  xref: 'x',
  y: paperY,
  yref: 'paper',
  text:
    `<b style="font-size:13px">${label}</b>` +
    `<br><span style="color:#555">Date :</span>  ${dateStr}` +
    valLine +
    (note ? `<br><span style="color:#555">Note :</span>  ${note}` : ''),
  showarrow: true,
  arrowhead: 2,
  arrowsize: 0.8,
  arrowwidth: 2,
  arrowcolor: color,
  ax: 0,
  ay: 36,
  xanchor: 'center',
  yanchor: 'top',
  cliponaxis: false,
  align: 'left',
  font: { size: 12, color: '#1a1a2e', family: 'Montserrat, sans-serif' },
  bgcolor: 'rgba(255,255,255,0.97)',
  bordercolor: color,
  borderwidth: 1.5,
  borderpad: 10,
});

export const buildEventTraces = ({
  items,
  tRaw,
  yRaw,
  donnees,
  yMax,
  domain,
  yaxisKey,
  markerSize,
  shownLabels,
  graphCurves,
}) => {
  const sorted = [...items]
    .map((ev, i) => ({ ...ev, _i: i }))
    .sort((a, b) => a.t - b.t);

  // Group-based stacking variables removed — all labels render at the same height (0.5)
  const traces = [];
  const annMeta = [];
  const images = [];
  const categories = [];
  const plainLabelColorMap = new Map(); // rawLabel → couleur unique
  // Two invisible anchor points spanning the full x range — prevents Plotly from
  // dimming dummy legend traces when hovermode:'x' is active.

  sorted.forEach((ev, i) => {
    const iconType = ev.iconType || ev.label;
    const xDate = convertiUtsToDate([ev.t])[0];
    const markerY = 0.5;

    // Recherche la courbe correspondant au capteur/position du label.
    let activeCurve = null;
    if (donnees?.length && ev.idSensor != null) {
      const sid = Number(ev.idSensor);
      const pos = Number(ev.positionInSensor ?? -1);
      activeCurve =
        donnees.find((d) => {
          const dPos = Number(
            d.position_in_sensor ?? d.channel ?? d.position ?? -1
          );
          return Number(d.id_sensor) === sid && dPos === pos;
        }) ?? null;
      if (!activeCurve && graphCurves?.length) {
        const gcIdx = graphCurves.findIndex((gc) => {
          const nums = (gc.formula || '').match(/\d+/g)?.map(Number);
          return nums?.length >= 2 && nums[0] === sid && nums[1] === pos;
        });
        if (gcIdx >= 0 && gcIdx < donnees.length) activeCurve = donnees[gcIdx];
      }
      if (!activeCurve) {
        const bySensor = donnees.filter((d) => Number(d.id_sensor) === sid);
        if (bySensor.length === 1) activeCurve = bySensor[0];
      }
    }
    const rawT = activeCurve ? activeCurve.x || activeCurve.t || tRaw : tRaw;
    const activeT = rawT.length && typeof rawT[0] === 'number' ? rawT : tRaw;
    const activeY = activeCurve ? activeCurve.y || [] : yRaw;

    let yCurve = yMax;
    if (activeT.length > 0) {
      let closest = 0,
        minDiff = Math.abs(activeT[0] - ev.t);
      for (let j = 1; j < activeT.length; j++) {
        const diff = Math.abs(activeT[j] - ev.t);
        if (diff < minDiff) {
          minDiff = diff;
          closest = j;
        }
      }
      yCurve = activeY[closest] ?? yMax;
    }

    const rawLabel = (
      ev.label_text ?? ev.label ?? iconType ?? 'Sans label'
    ).trim();
    const displayLabel =
      rawLabel.length > 22 ? `${rawLabel.slice(0, 22)}...` : rawLabel;

    // Label MANUEL (Jalon) → toujours un cercle (jamais de SVG), couleur réservée fixe.
    const isManual = ev.labelType === 'JALON' || ev.source === 'MANUAL' || iconType === 'diamond';
    // Resolve SVG inheritance: subcategories point to their parent's ICON_PATHS key.
    const resolvedIcon = CATEGORY_SVG_KEYS[iconType] ?? iconType;
    const svgUri = isManual ? null : buildIconDataUri(resolvedIcon, '#000');
    const hasSvg = !!svgUri;
    // Clé de couleur/groupe : par catégorie si SVG, sinon par texte de label.
    const colorKey = hasSvg ? resolvedIcon : rawLabel;
    let color;
    if (isManual) {
      color = MANUAL_COLOR;
    } else {
      if (!plainLabelColorMap.has(colorKey)) {
        plainLabelColorMap.set(colorKey, LABEL_COLORS[plainLabelColorMap.size % LABEL_COLORS.length]);
      }
      color = plainLabelColorMap.get(colorKey);
    }

    // Groupe de légende : par catégorie (SVG) ou par texte de label (cercle)
    const legendGroup = hasSvg ? `category:${resolvedIcon}` : `label:${rawLabel}`;
    const isFirstOccurrence =
      !shownLabels.has(legendGroup) &&
      (() => { shownLabels.add(legendGroup); return true; })();

    // Seuls les labels avec SVG vont dans `categories` (légende à icône).
    // Les autres (cercles, dont les Jalons manuels) sont gérés via plainLabelItems.
    if (isFirstOccurrence && hasSvg) {
      categories.push({ name: resolvedIcon, color, iconType: resolvedIcon });
    }

    if (hasSvg) {
      // Le SVG visible est rendu en overlay DOM (useIconOverlay) à taille pixel fixe,
      // garantissant la même taille que le cercle. Ici on ne pousse QU'UN marker invisible
      // (hitbox) pour conserver le hover/click — pas d'image en coordonnées données (taille incohérente).
      traces.push({
        type: 'scatter', mode: 'markers',
        name: '', legendgroup: legendGroup, showlegend: false,
        xaxis: 'x', yaxis: yaxisKey,
        x: [xDate], y: [markerY],
        cliponaxis: false, hoverinfo: 'none',
        customdata: [{ type: 'label', t: ev.t, annMetaIdx: i, itemIdx: ev._i }],
        marker: { size: markerSize, color: 'rgba(0,0,0,0)', symbol: 'circle', line: { width: 0 } },
      });
    } else {
      // Pas de SVG → cercle coloré standard
      traces.push({
        type: 'scatter', mode: 'markers',
        name: displayLabel, legendgroup: legendGroup, showlegend: false,
        xaxis: 'x', yaxis: yaxisKey,
        x: [xDate], y: [markerY],
        cliponaxis: false, hoverinfo: 'none',
        customdata: [{ type: 'label', t: ev.t, annMetaIdx: i, itemIdx: ev._i }],
        marker: { size: markerSize, color, symbol: 'circle', line: { color, width: 2 } },
      });
    }

    const dateStr = new Date(ev.t * 1000).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
    const valLine = `<br><span style="color:#555">Valeur :</span>  <b>${Math.round(yCurve * 10) / 10}</b>`;

    annMeta.push({
      xDate, t: ev.t, markerY,
      style: { color },
      legendGroup,
      iconType: resolvedIcon,
      hasSvg: !!svgUri,
      label: displayLabel,
      note: ev.note,
      dateStr, yCurveVal: yCurve, valLine, domain, itemIdx: ev._i,
    });
  });

  return { traces, annMeta, images, categories };
};
